import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { alertsAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface EmailHistoryItem {
  id: number;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  alert_type: string;
  alert_severity: string;
  status: string;
  sent_at: string | null;
  created_at: string;
  error_message: string | null;
  email_type: string;
  priority: string;
  attempts: number;
  metadata: any;
}

const DATE_PRESETS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 6 months', value: 180 },
  { label: 'Last 1 year', value: 365 },
];

const AlertSendHistoryPage: React.FC = () => {
  useDocumentTitle('Email Notifications');
  const [history, setHistory] = useState<EmailHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('alert');
  const [daysFilter, setDaysFilter] = useState<number>(90);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Debounce search input
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const filters: any = { days: daysFilter, limit: 100 };

      if (selectedFilter && selectedFilter !== 'all') {
        filters.status = selectedFilter;
      }
      if (emailTypeFilter) {
        filters.email_type = emailTypeFilter;
      }
      if (debouncedSearch.trim()) {
        filters.search = debouncedSearch.trim();
      }

      const response = await alertsAPI.getEmailHistory(filters);
      if (response.data.status === 'success') {
        setHistory(response.data.data.history || []);
        setTotal(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch email history:', error);
      toast.error('Failed to load email history');
    } finally {
      setLoading(false);
    }
  }, [selectedFilter, emailTypeFilter, daysFilter, debouncedSearch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleViewDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      const response = await alertsAPI.getEmailHistoryRecord(id);
      if (response.data.status === 'success') {
        setSelectedEmail(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch email details:', error);
      toast.error('Failed to load email details');
    } finally {
      setDetailLoading(false);
    }
  };

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-danger',
      error: 'bg-warning text-dark',
      warning: 'bg-info text-dark',
      info: 'bg-secondary',
    };
    return colors[severity] || 'bg-secondary';
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-secondary',
      processing: 'bg-info text-dark',
      sent: 'bg-success',
      failed: 'bg-danger',
      cancelled: 'bg-dark',
    };
    return colors[status] || 'bg-secondary';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      pending: 'bi-clock',
      processing: 'bi-arrow-repeat',
      sent: 'bi-check-circle',
      failed: 'bi-x-circle',
      cancelled: 'bi-slash-circle',
    };
    return icons[status] || 'bi-circle';
  };

  const getEmailTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      alert: 'Alert',
      notification: 'Notification',
      otp: 'OTP',
      invitation: 'Invitation',
      welcome: 'Welcome',
      password_reset: 'Password Reset',
      contact: 'Contact',
      other: 'Other',
    };
    return labels[type] || type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getEmailTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      alert: 'text-danger',
      notification: 'text-primary',
      otp: 'text-info',
      invitation: 'text-success',
      welcome: 'text-success',
      password_reset: 'text-warning',
    };
    return colors[type] || 'text-secondary';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return '';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const hasActiveFilters = selectedFilter !== 'all' || emailTypeFilter !== 'alert' || daysFilter !== 90 || searchQuery !== '';

  return (
    <DashboardLayout>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">
              <i className="bi bi-envelope-paper me-2"></i>
              Email Notifications
            </h4>
            <p className="text-muted mb-0">Track all email notifications sent to your account</p>
          </div>
          <button className="btn btn-outline-primary btn-sm" onClick={fetchHistory} disabled={loading}>
            <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'spin' : ''}`}></i>
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              {/* Search */}
              <div className="col-md-4">
                <label className="form-label small text-muted mb-1">Search Subject</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by subject..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchQuery('')}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
              {/* Date Range */}
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Date Range</label>
                <select
                  className="form-select form-select-sm"
                  value={daysFilter}
                  onChange={(e) => setDaysFilter(Number(e.target.value))}
                >
                  {DATE_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Status */}
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              {/* Type */}
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Type</label>
                <select
                  className="form-select form-select-sm"
                  value={emailTypeFilter}
                  onChange={(e) => setEmailTypeFilter(e.target.value)}
                >
                  <option value="alert">Alerts only</option>
                  <option value="">All Types</option>
                  <option value="notification">Notifications</option>
                  <option value="otp">OTP</option>
                  <option value="invitation">Invitations</option>
                  <option value="welcome">Welcome</option>
                  <option value="password_reset">Password Reset</option>
                </select>
              </div>
              {/* Clear */}
              <div className="col-md-2 d-flex align-items-end">
                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => {
                      setSelectedFilter('all');
                      setEmailTypeFilter('alert');
                      setDaysFilter(90);
                      setSearchQuery('');
                    }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Email History List */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Email History
            </h6>
            {!loading && (
              <small className="text-muted">
                Showing {history.length} of {total} emails
                {daysFilter && ` (last ${daysFilter} days)`}
              </small>
            )}
          </div>

          {loading ? (
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3 mb-0">Loading email history...</p>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                </div>
                <h6 className="text-muted mb-2">No email notifications found</h6>
                <p className="text-muted small mb-3">
                  {hasActiveFilters
                    ? 'No emails match your current filters. Try adjusting or clearing the filters.'
                    : 'No alert emails found. Alert emails are sent for server downtime, high resource usage, and security threats. Use the Type filter above to view all email types.'
                  }
                </p>
                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      setSelectedFilter('all');
                      setEmailTypeFilter('alert');
                      setDaysFilter(90);
                      setSearchQuery('');
                    }}
                  >
                    <i className="bi bi-funnel me-1"></i>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 ps-3" style={{ width: '35px' }}></th>
                    <th className="border-0">Subject</th>
                    <th className="border-0">Type</th>
                    <th className="border-0">Priority</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Date</th>
                    <th className="border-0" style={{ width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td className="ps-3">
                        <i className={`bi ${getStatusIcon(item.status)} ${
                          item.status === 'sent' ? 'text-success' :
                          item.status === 'failed' ? 'text-danger' :
                          item.status === 'pending' ? 'text-muted' : 'text-info'
                        }`}></i>
                      </td>
                      <td>
                        <div>
                          <div className="text-truncate" style={{ maxWidth: '350px' }} title={item.subject}>
                            {item.subject}
                          </div>
                          {item.error_message && (
                            <small className="text-danger d-block text-truncate" style={{ maxWidth: '350px' }}>
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              {item.error_message}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`small fw-medium ${getEmailTypeColor(item.email_type)}`}>
                          <i className={`bi ${
                            item.email_type === 'alert' ? 'bi-bell' :
                            item.email_type === 'otp' ? 'bi-shield-lock' :
                            item.email_type === 'invitation' ? 'bi-person-plus' :
                            item.email_type === 'welcome' ? 'bi-hand-thumbs-up' :
                            item.email_type === 'password_reset' ? 'bi-key' :
                            'bi-envelope'
                          } me-1`}></i>
                          {getEmailTypeLabel(item.email_type)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getSeverityBadge(item.alert_severity)} rounded-pill`}>
                          {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(item.status)} rounded-pill`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        {item.attempts > 1 && (
                          <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                            {item.attempts} attempts
                          </small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted" title={formatDate(item.sent_at || item.created_at)}>
                          {formatRelativeTime(item.sent_at || item.created_at)}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleViewDetail(item.id)}
                          title="View email details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Email Detail Modal */}
        {selectedEmail && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-envelope-open me-2"></i>
                    Email Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedEmail(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  {detailLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">To</label>
                            <div>{selectedEmail.recipient_email}</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">Status</label>
                            <div>
                              <span className={`badge ${getStatusBadge(selectedEmail.status)}`}>
                                {selectedEmail.status}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">Type</label>
                            <div>{getEmailTypeLabel(selectedEmail.email_type)}</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">Priority</label>
                            <div>{selectedEmail.priority}</div>
                          </div>
                          <div className="col-12">
                            <label className="form-label small text-muted mb-0">Subject</label>
                            <div className="fw-medium">{selectedEmail.subject}</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">Created</label>
                            <div className="small">{formatDate(selectedEmail.created_at)}</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small text-muted mb-0">Sent</label>
                            <div className="small">{formatDate(selectedEmail.sent_at)}</div>
                          </div>
                          {selectedEmail.error_message && (
                            <div className="col-12">
                              <label className="form-label small text-muted mb-0">Error</label>
                              <div className="text-danger small">{selectedEmail.error_message}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedEmail.body_html && (
                        <div className="border rounded mt-3">
                          <div className="p-2 bg-light border-bottom">
                            <small className="text-muted fw-medium">Email Content</small>
                          </div>
                          <div
                            className="p-3"
                            style={{ maxHeight: '400px', overflow: 'auto' }}
                            dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedEmail(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AlertSendHistoryPage;
